import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, RefreshCw, ShieldCheck, Trash2 } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { useEffectivePermissions } from '../hooks/useEffectivePermissions';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { timeZone: 'America/Chicago', dateStyle: 'medium', timeStyle: 'medium' }) : '—');

function PurgePanel({ onDone }) {
  const [scope, setScope] = useState([]);
  const [clientIds, setClientIds] = useState(new Set());
  const [siteIds, setSiteIds] = useState(new Set());
  const [preview, setPreview] = useState(null);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/gsoc/admin/purge-scope')
    .then(({ data }) => setScope(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = (set, id) => {
    const next = new Set(set);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  };
  const pickClient = (c) => {
    setPreview(null);
    setClientIds((s) => toggle(s, c.id));
  };
  const pickSite = (s) => {
    setPreview(null);
    setSiteIds((v) => toggle(v, s.id));
  };

  const body = { client_ids: [...clientIds], site_ids: [...siteIds] };
  const nothingPicked = body.client_ids.length === 0 && body.site_ids.length === 0;

  const q = filter.trim().toLowerCase();
  const shown = !q ? scope : scope
    .map((c) => c.name.toLowerCase().includes(q)
      ? c
      : { ...c, sites: c.sites.filter((s) => s.name.toLowerCase().includes(q)) })
    .filter((c) => c.name.toLowerCase().includes(q) || c.sites.length > 0);

  const selectEmptySites = () => {
    const empties = shown.flatMap((c) => c.sites.filter((s) => s.entry_count === 0).map((s) => s.id));
    if (empties.length === 0) return toast.info('Every site in view has entries against it');
    setSiteIds((v) => new Set([...v, ...empties]));
    setPreview(null);
  };

  const dryRun = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/gsoc/admin/purge-selection', body);
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not work out what would be removed');
    } finally { setBusy(false); }
  };

  const commit = async () => {
    if (!window.confirm(`Permanently delete ${preview.entries_to_delete} log entr${preview.entries_to_delete === 1 ? 'y' : 'ies'}, `
      + `${preview.clients.length} client(s) and ${preview.sites.length} site(s)? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post('/gsoc/admin/purge-selection?confirm=true', body);
      toast.success(`Removed ${data.deleted_entries} entries, ${data.deleted_clients} clients and ${data.deleted_sites} sites`);
      setClientIds(new Set()); setSiteIds(new Set()); setPreview(null);
      await load();
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not complete the purge');
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-line bg-surface p-5 space-y-4" data-testid="gsoc-purge-panel">
      <div>
        <p className="font-medium text-fg">Remove GSOC clients, sites and their entries</p>
        <p className="text-sm text-fg-secondary mt-1">
          For clearing seeded or test data out of the record. Ticking a client takes its sites and every
          log entry recorded against it. The hash chain is rebuilt over what survives and the removal is
          filed below and in the audit log. There is no undo.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <input value={filter} onChange={(e) => setFilter(e.target.value)}
               placeholder="Filter by client or site name"
               className="h-9 px-3 text-sm rounded-md border border-line bg-surface text-fg flex-1 min-w-[220px]"
               data-testid="gsoc-purge-filter" />
        <Button variant="outline" size="sm" onClick={selectEmptySites} data-testid="gsoc-purge-select-empty-btn">
          Tick every site with no entries
        </Button>
        {(clientIds.size > 0 || siteIds.size > 0) && (
          <Button variant="ghost" size="sm" onClick={() => { setClientIds(new Set()); setSiteIds(new Set()); setPreview(null); }}
                  data-testid="gsoc-purge-clear-btn">
            Clear {clientIds.size + siteIds.size} selected
          </Button>
        )}
      </div>

      <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="gsoc-purge-scope">
        {shown.length === 0 && <p className="text-sm text-fg-muted">Nothing matches that filter.</p>}
        {shown.map((c, i) => (
          <div key={c.id} className="rounded-md border border-line-subtle p-3">
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={clientIds.has(c.id)} onChange={() => pickClient(c)}
                     className="mt-1 w-4 h-4 accent-[hsl(var(--danger))]"
                     data-testid={`gsoc-purge-client-${i}`} />
              <span>
                <span className="font-medium text-fg">{c.name}</span>
                {!c.is_active && <span className="ml-2 text-xs text-fg-muted">(inactive)</span>}
                <span className="block text-xs text-fg-muted">
                  {c.sites.length} site{c.sites.length === 1 ? '' : 's'} · {c.entry_count} log
                  entr{c.entry_count === 1 ? 'y' : 'ies'} · {c.pass_on_count} pass-on note{c.pass_on_count === 1 ? '' : 's'}
                </span>
              </span>
            </label>
            {c.sites.length > 0 && !clientIds.has(c.id) && (
              <div className="mt-2 ml-6 space-y-1">
                {c.sites.map((s, j) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={siteIds.has(s.id)} onChange={() => pickSite(s)}
                           className="w-4 h-4 accent-[hsl(var(--danger))]"
                           data-testid={`gsoc-purge-site-${i}-${j}`} />
                    <span className="text-fg-secondary">
                      {s.name}
                      <span className="text-xs text-fg-muted ml-2">
                        {s.entry_count} entr{s.entry_count === 1 ? 'y' : 'ies'}{s.is_active ? '' : ' · inactive'}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {preview && (
        <div className="rounded-md border border-warning-border bg-warning-bg p-4 text-sm" data-testid="gsoc-purge-preview">
          <p className="font-medium text-fg">
            This will delete {preview.entries_to_delete} log entr{preview.entries_to_delete === 1 ? 'y' : 'ies'},
            {' '}{preview.clients.length} client{preview.clients.length === 1 ? '' : 's'} and
            {' '}{preview.sites.length} site{preview.sites.length === 1 ? '' : 's'}.
          </p>
          <p className="text-fg-secondary mt-1">
            {preview.entries_remaining} entr{preview.entries_remaining === 1 ? 'y' : 'ies'} will remain and be relinked.
            {preview.clients.length > 0 && <> Clients: {preview.clients.join(', ')}.</>}
          </p>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={dryRun} disabled={busy || nothingPicked}
                data-testid="gsoc-purge-preview-btn">
          {busy ? 'Working...' : 'Preview what will be removed'}
        </Button>
        {preview && (
          <Button onClick={commit} disabled={busy}
                  className="bg-[hsl(var(--danger))] hover:bg-[hsl(var(--danger))]/90 text-white"
                  data-testid="gsoc-purge-commit-btn">
            <Trash2 className="w-4 h-4 mr-2" aria-hidden="true" />
            Permanently remove
          </Button>
        )}
      </div>
    </div>
  );
}

export default function GSOCIntegrity() {
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [demo, setDemo] = useState({ demo_entries: 0, rebuilds: [] });
  const [purging, setPurging] = useState(false);
  const perms = useEffectivePermissions();
  const canPurge = perms.role === 'admin' || perms.can('gsoc_config', 'delete');

  const loadDemo = () => api.get('/gsoc/admin/demo-data-status')
    .then(({ data }) => setDemo(data)).catch(() => {});

  const purge = async () => {
    if (!window.confirm(`Permanently remove ${demo.demo_entries} seeded demo entries and relink the hash chain? Real entries are not affected.`)) return;
    setPurging(true);
    try {
      const { data } = await api.post('/gsoc/admin/purge-demo-data?confirm=true');
      toast.success(`${data.deleted} demo entries removed, ${data.relinked} entries relinked`);
      await loadDemo();
      await run();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove the demo data');
    } finally { setPurging(false); }
  };

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.get('/gsoc/integrity');
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not run the integrity check');
    } finally { setRunning(false); }
  };

  useEffect(() => { run(); loadDemo(); }, []);

  return (
    <div className="max-w-4xl space-y-5" data-testid="gsoc-integrity-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="page-title">Log Integrity</h1>
          <p className="text-sm text-fg-muted mt-1 max-w-xl">
            Every entry is hashed together with the hash of the entry before it. Walking that chain proves
            no row was altered, deleted or reordered — including directly in the database.
          </p>
        </div>
        <Button onClick={run} disabled={running} data-testid="gsoc-integrity-run-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${running ? 'animate-spin' : ''}`} />
          {running ? 'Verifying...' : 'Re-run Check'}
        </Button>
      </div>

      {result && (
        <div className={`rounded-lg border p-5 ${result.intact ? 'border-success-border bg-success-bg' : 'border-danger-border bg-danger-bg'}`}
             data-testid="gsoc-integrity-result">
          <div className="flex items-start gap-3">
            {result.intact
              ? <ShieldCheck className="w-8 h-8 text-success shrink-0" aria-hidden="true" />
              : <AlertTriangle className="w-8 h-8 text-danger shrink-0" aria-hidden="true" />}
            <div>
              <p className="text-lg font-semibold text-fg" data-testid="gsoc-integrity-status">
                {result.intact ? 'Chain intact — no tampering detected' : 'Chain broken — the log was altered'}
              </p>
              <p className="text-sm text-fg-secondary mt-1">
                {result.entries_checked} entr{result.entries_checked === 1 ? 'y' : 'ies'} verified at {fmt(result.checked_at)} Central.
                {!result.intact && ` First break at entry #${result.first_break_entry_number}.`}
              </p>
            </div>
          </div>
        </div>
      )}

      {result?.breaks?.length > 0 && (
        <div className="rounded-lg border border-line bg-surface overflow-hidden" data-testid="gsoc-integrity-breaks">
          <div className="px-4 py-2 text-sm font-medium border-b border-line">Broken links</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-surface-sunken">
                <th className="text-left px-4 py-2 font-medium text-fg-muted">Entry #</th>
                <th className="text-left px-4 py-2 font-medium text-fg-muted">Problem</th>
              </tr>
            </thead>
            <tbody>
              {result.breaks.map((b) => (
                <tr key={b.entry_id} className="border-b border-line-subtle" data-testid={`gsoc-break-${b.entry_number}`}>
                  <td className="px-4 py-2 tabular-nums font-medium">{b.entry_number}</td>
                  <td className="px-4 py-2 text-fg-secondary">{b.problem}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {demo.demo_entries > 0 && canPurge && (
        <div className="rounded-lg border border-warning-border bg-warning-bg p-5" data-testid="gsoc-demo-purge-card">
          <div className="flex items-start gap-3">
            <Trash2 className="w-6 h-6 text-warning shrink-0" aria-hidden="true" />
            <div className="flex-1">
              <p className="font-semibold text-fg">
                {demo.demo_entries} seeded demo {demo.demo_entries === 1 ? 'entry' : 'entries'} in this log
              </p>
              <p className="text-sm text-fg-secondary mt-1">
                These were sample entries created when the module was first switched on. Removing them also
                relinks the hash chain over the remaining entries, and the removal is recorded below and in the
                audit log. Real operator entries are never touched.
              </p>
              <Button className="mt-3" onClick={purge} disabled={purging} data-testid="gsoc-demo-purge-btn">
                {purging ? 'Removing...' : `Remove ${demo.demo_entries} demo ${demo.demo_entries === 1 ? 'entry' : 'entries'}`}
              </Button>
            </div>
          </div>
        </div>
      )}

      {canPurge && <PurgePanel onDone={() => { loadDemo(); run(); }} />}

      {demo.rebuilds?.length > 0 && (
        <div className="rounded-lg border border-line bg-surface p-5 text-sm space-y-2" data-testid="gsoc-rebuild-history">
          <p className="font-medium text-fg">Chain rebuild history</p>
          {demo.rebuilds.map((r) => {
            const nums = r.entry_numbers_deleted || [];
            const shown = nums.slice(0, 10).map((n) => `#${n}`).join(', ');
            return (
              <p key={r.id} className="text-fg-secondary">
                {fmt(r.performed_at)} — {r.performed_by_name} removed {r.entries_deleted}{' '}
                {r.entries_deleted === 1 ? 'entry' : 'entries'}
                {nums.length > 0 && <> ({shown}{nums.length > 10 ? ` and ${nums.length - 10} more` : ''})</>}
                {r.clients_deleted?.length > 0 && <>, clients: {r.clients_deleted.join(', ')}</>}
                {' '}and relinked {r.entries_relinked}. Reason: {r.reason}.
              </p>
            );
          })}
        </div>
      )}

      <div className="rounded-lg border border-line bg-surface p-5 text-sm text-fg-secondary space-y-2">
        <p className="font-medium text-fg">What this check cannot do</p>
        <p>
          It proves the record has not been changed since it was written. It cannot prove an operator typed the truth —
          that is what the timestamps, the shift stamp and the named operator on every entry are for.
        </p>
        <p>
          Every run of this check is written to the audit log, so there is a record of who verified the chain and when.
        </p>
      </div>
    </div>
  );
}
